import { ethers } from 'ethers6';
import { config } from './config';
import { GelatoRelay } from '@gelatonetwork/relay-sdk';

/**
 * Temporary Wallet Deployment
 * 
 * Generates a temporary wallet for users without Web3 wallets.
 * The private key is shown ONCE and never stored.
 * User is responsible for saving it to claim creator fees.
 */

export interface TemporaryWalletDeploymentResult {
  success: boolean;
  tokenAddress?: string;
  poolAddress?: string;
  nftTokenId?: string;
  transactionHash?: string;
  privateKey?: string;
  walletAddress?: string;
  error?: string;
}

/**
 * Deploy a token using a temporary generated wallet
 * @param params Token deployment parameters
 * @returns Promise with deployment result including private key
 */
export async function deployWithTemporaryWallet(params: {
  name: string;
  symbol: string;
  npmAddress: string;
}): Promise<TemporaryWalletDeploymentResult> {
  try {
    console.log('🔑 Generating temporary wallet for deployment...');

    // Generate a random wallet (client-side only, never stored)
    const tempWallet = ethers.Wallet.createRandom();
    const walletAddress = tempWallet.address;
    const privateKey = tempWallet.privateKey;

    console.log('✅ Temporary wallet generated:', walletAddress);
    console.log('⚠️ Private key captured (length:', privateKey.length, 'chars) - will be shown ONCE to user');

    // Connect wallet to provider (needed for Gelato SDK)
    const provider = new ethers.JsonRpcProvider(config.ink.primaryRpcUrl, {
      chainId: config.ink.chainId,
      name: 'Ink L2'
    });
    const connectedWallet = tempWallet.connect(provider);
    
    // Verify wallet can sign (proves it has private key access)
    const testMessage = 'test';
    try {
      const testSignature = await connectedWallet.signMessage(testMessage);
      console.log('🔐 Wallet signing verified - signature length:', testSignature.length, 'chars');
      console.log('✅ Gelato SDK will be able to access private key for signing');
    } catch (e) {
      console.error('❌ Wallet cannot sign - private key not accessible!');
      throw new Error('Wallet signing test failed');
    }

    // Initialize Gelato Relay
    const relay = new GelatoRelay({
      contract: {
        relayERC2771: config.gelato.trustedForwarder,
        relay1BalanceERC2771: config.gelato.trustedForwarder,
        relayERC2771zkSync: config.gelato.trustedForwarder,
        relayERC2771Abstract: config.gelato.trustedForwarder,
        relay1BalanceERC2771zkSync: config.gelato.trustedForwarder,
        relay1BalanceERC2771Abstract: config.gelato.trustedForwarder,
        relayConcurrentERC2771: config.gelato.trustedForwarder,
        relayConcurrentERC2771zkSync: config.gelato.trustedForwarder,
        relayConcurrentERC2771Abstract: config.gelato.trustedForwarder,
        relay1BalanceConcurrentERC2771: config.gelato.trustedForwarder,
        relay1BalanceConcurrentERC2771zkSync: config.gelato.trustedForwarder,
        relay1BalanceConcurrentERC2771Abstract: config.gelato.trustedForwarder,
      }
    });

    // Create factory interface for encoding (V2)
    const factoryInterface = new ethers.Interface([
      {
        "inputs": [
          {"internalType": "string", "name": "_name", "type": "string"},
          {"internalType": "string", "name": "_symbol", "type": "string"},
          {"internalType": "address", "name": "npm", "type": "address"}
        ],
        "name": "newMavrkToken",
        "outputs": [],
        "stateMutability": "nonpayable",
        "type": "function"
      },
      {
        "anonymous": false,
        "inputs": [
          {"indexed": false, "internalType": "address", "name": "tokenAddress", "type": "address"},
          {"indexed": false, "internalType": "string", "name": "name", "type": "string"},
          {"indexed": false, "internalType": "string", "name": "symbol", "type": "string"},
          {"indexed": true, "internalType": "address", "name": "creator", "type": "address"}
        ],
        "name": "TokenDeployed",
        "type": "event"
      },
      {
        "anonymous": false,
        "inputs": [
          {"indexed": false, "internalType": "address", "name": "pool", "type": "address"}
        ],
        "name": "PoolInitialized",
        "type": "event"
      },
      {
        "anonymous": false,
        "inputs": [
          {"indexed": false, "internalType": "uint256", "name": "tokenId", "type": "uint256"},
          {"indexed": true, "internalType": "address", "name": "creator", "type": "address"}
        ],
        "name": "LiquidityMinted",
        "type": "event"
      },
      {
        "anonymous": false,
        "inputs": [
          {"indexed": false, "internalType": "uint256", "name": "tokenId", "type": "uint256"},
          {"indexed": true, "internalType": "address", "name": "creator", "type": "address"}
        ],
        "name": "NFTLocked",
        "type": "event"
      }
    ]);

    // Encode the function call data (V2 with npm address)
    const functionSignature = 'newMavrkToken(string,string,address)';
    const abiCoder = new ethers.AbiCoder();
    
    // Create function selector
    const functionSelector = ethers.id(functionSignature).slice(0, 10);
    
    // Encode parameters
    const encodedParams = abiCoder.encode(
      ['string', 'string', 'address'],
      [params.name, params.symbol, params.npmAddress]
    ).slice(2);

    // Combine selector and params
    const data = functionSelector + encodedParams;

    // Use V2 factory address
    const factoryAddress = '0xE01118dbcF8Ba2609d3ffBD6A53d9533c7526D60';

    console.log('📡 Temporary wallet Gelato deployment (V2):', {
      name: params.name,
      symbol: params.symbol,
      npmAddress: params.npmAddress,
      walletAddress: walletAddress,
      factoryAddress: factoryAddress,
    });

    // Prepare ERC-2771 request
    const request = {
      chainId: BigInt(config.ink.chainId),
      target: factoryAddress as `0x${string}`,
      data: data as `0x${string}`,
      user: walletAddress as `0x${string}`,
      userDeadline: Math.floor(Date.now() / 1000) + 300, // 5 minutes
    };

    console.log('📡 Sending ERC-2771 sponsored call (temp wallet signing)...');

    // Send via Gelato Relay (connected wallet signs)
    const response = await relay.sponsoredCallERC2771(
      request,
      connectedWallet as any,
      config.gelato.apiKey
    );

    if (!response || !response.taskId) {
      throw new Error('Gelato relay failed to return taskId');
    }

    console.log('✅ Gelato task created:', response.taskId);
    console.log('⏳ Waiting for transaction to be mined...');

    // Poll for task completion
    let taskStatus;
    let attempts = 0;
    const maxAttempts = 60;

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const statusResponse = await fetch(
        `https://relay.gelato.digital/tasks/status/${response.taskId}`
      );
      
      taskStatus = await statusResponse.json();
      
      const taskState = taskStatus.task?.taskState;
      console.log(`⏳ Task status: ${taskState || 'Pending'} (${attempts + 1}/${maxAttempts})`);
      
      if (taskState === 'ExecSuccess' || taskState === 'CheckSuccess') {
        console.log('✅ Gelato transaction confirmed!');
        break;
      } else if (taskState === 'ExecReverted' || taskState === 'Cancelled') {
        throw new Error(`Gelato task failed: ${taskState}`);
      }
      
      attempts++;
    }

    if (!taskStatus?.task?.transactionHash) {
      throw new Error('Failed to get transaction hash from Gelato');
    }

    const txHash = taskStatus.task.transactionHash;
    console.log('✅ Deployment confirmed:', txHash);
    console.log('📋 Parsing transaction logs...');

    // Get transaction receipt
    const receipt = await provider.getTransactionReceipt(txHash);
    
    if (!receipt) {
      throw new Error('Failed to get transaction receipt');
    }

    // Parse logs to extract addresses
    let tokenAddress: string | undefined;
    let poolAddress: string | undefined;
    let nftTokenId: string | undefined;

    console.log(`Found ${receipt.logs.length} logs in transaction`);

    for (const log of receipt.logs) {
      try {
        const parsedLog = factoryInterface.parseLog({
          topics: [...log.topics],
          data: log.data
        });
        
        if (parsedLog) {
          console.log(`📝 Parsed event: ${parsedLog.name}`, parsedLog.args);
          
          if (parsedLog.name === 'TokenDeployed') {
            tokenAddress = parsedLog.args.tokenAddress;
            console.log('✅ Token Address:', tokenAddress);
          }
          if (parsedLog.name === 'PoolInitialized') {
            poolAddress = parsedLog.args.pool;
            console.log('✅ Pool Address:', poolAddress);
          }
          if (parsedLog.name === 'LiquidityMinted') {
            nftTokenId = parsedLog.args.tokenId.toString();
            console.log('✅ NFT Token ID:', nftTokenId);
          }
          if (parsedLog.name === 'NFTLocked' && !nftTokenId) {
            nftTokenId = parsedLog.args.tokenId.toString();
            console.log('✅ NFT Token ID:', nftTokenId);
          }
        }
      } catch (e) {
        // Log might not be from our factory contract
        console.log('⚠️ Could not parse log (might be from another contract)');
      }
    }

    console.log('📊 Parsed deployment data:', { tokenAddress, poolAddress, nftTokenId });

    // Return result WITH private key (shown once to user)
    console.log('🔑 Returning private key to frontend (length:', privateKey.length, 'chars)');
    console.log('⚠️ Private key will be displayed in modal for user to save');
    
    return {
      success: true,
      tokenAddress,
      poolAddress,
      nftTokenId,
      transactionHash: txHash,
      privateKey, // ⚠️ User must save this!
      walletAddress,
    };

  } catch (error: any) {
    console.error('❌ Temporary wallet deployment failed:', error);
    return {
      success: false,
      error: error.message || 'Deployment failed'
    };
  }
}

